import { Fragment, useState } from "react";
import {
  Badge,
  Collapse,
  Table,
  Button,
  Spinner,
} from "reactstrap";
import { ChevronDown, ChevronRight } from "react-feather";
import { getQuestionType } from "Utils/getQuestionType";
import { aiAuditCategoryOptions } from "Constant/ReportType";
import TryoutAIAuditQuestionDetailModal from "./TryoutAIAuditQuestionDetailModal";
import {
  formatConfidence,
  capitalize,
  severityColor,
} from "../TryoutAIAudit.utils";

// Baris detail collapsible untuk satu hasil audit.
const TryoutAIAuditDetailRow = ({ item, isOpen, onOpenQuestionDetail }) => {
  return (
    <tr>
      <td colSpan={9} style={{ padding: 0, border: 0 }}>
        <Collapse isOpen={isOpen}>
          <div className="p-3 bg-white border-top" style={{ borderColor: "#e4ebf7" }}>
            <div className="mb-3">
              <div className="d-flex justify-content-between small text-muted mb-1">
                <span className="fw-semibold">Confidence</span>
                <span>{formatConfidence(item.confidence)}</span>
              </div>
              <div className="progress rounded-pill" style={{ height: 6, background: "#eef2ff" }}>
                <div
                  className={`progress-bar bg-${severityColor(item.severity)}`}
                  style={{ width: formatConfidence(item.confidence) }}
                />
              </div>
            </div>

            <div className="mb-2">
              <small className="text-muted fw-semibold">Pesan Audit:</small>
              <div
                className="border rounded p-2 text-dark"
                style={{ whiteSpace: "pre-wrap", background: "#f8fbff" }}
              >
                {item.message ?? "-"}
              </div>
            </div>

            <div className="mt-3 d-flex flex-wrap gap-2">
              <Button
                color="primary"
                outline
                size="sm"
                className="rounded-pill px-3"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenQuestionDetail(item);
                }}
              >
                Lihat Detail Soal
              </Button>
            </div>

            {item.question?.description && (
              <div className="mt-3 small text-muted">
                <span className="fw-semibold">Deskripsi Singkat:</span>{" "}
                <span dangerouslySetInnerHTML={{ __html: item.question.description }} />
              </div>
            )}
            <div className="row g-2 small text-muted mt-2">
              <div className="col-md-4">
                <span className="fw-semibold">Created By:</span>{" "}
                {item.created_by ?? "-"}
              </div>
              <div className="col-md-4">
                <span className="fw-semibold">Created At:</span>{" "}
                {item.created_at
                  ? new Date(item.created_at).toLocaleString("id-ID")
                  : "-"}
              </div>
            </div>
            <div className="row g-2 small text-muted mt-2">
              <div className="col-md-4">
                <span className="fw-semibold">Interactive:</span>{" "}
                {item.question?.is_interactive ? (
                  <Badge color="success">Ya</Badge>
                ) : (
                  <Badge color="secondary">Tidak</Badge>
                )}
              </div>
              <div className="col-md-4">
                <span className="fw-semibold">Interactive Type:</span>{" "}
                {item.question?.interactive_type ? (
                  <Badge color="info">{item.question.interactive_type}</Badge>
                ) : (
                  "-"
                )}
              </div>
            </div>
            {item.is_solved && (item.solved_by || item.solved_at) && (
              <div className="mt-2 small text-muted">
                Diselesaikan oleh{" "}
                {item.solved_by?.name ?? item.solved_by}
                {item.solved_at
                  ? ` ${new Date(item.solved_at).toLocaleString("id-ID")}`
                  : " -"}
              </div>
            )}
          </div>
        </Collapse>
      </td>
    </tr>
  );
};

// Tabel hasil audit dengan baris collapsible.
const TryoutAIAuditTable = ({ resultsList, openId, onToggleRow, onSolveClick, solvingIds }) => {
  const [detailModal, setDetailModal] = useState({ isOpen: false, item: null });

  const handleOpenQuestionDetail = (auditItem) => {
    setDetailModal({ isOpen: true, item: auditItem });
  };

  const handleCloseQuestionDetail = () => {
    setDetailModal({ isOpen: false, item: null });
  };

  const questionDetail = detailModal.item?.question;

  return (
    <>
      <div className="table-responsive rounded-4 border overflow-hidden" style={{ borderColor: "#e4ebf7" }}>
        <Table hover borderless className="align-middle mb-0">
          <thead style={{ background: "#f8fafc" }}>
            <tr>
              <th style={{ width: "2.5rem" }}>No</th>
              <th style={{ width: "5rem" }}>ID</th>
              <th>Soal</th>
              <th>Kategori Soal</th>
              <th>Tipe Soal</th>
              <th>Masalah</th>
              <th>Severity</th>
              <th style={{ width: "8rem" }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {resultsList.map((item, idx) => {
              const isOpen = openId === item.id;
              return (
                <Fragment key={item.id ?? item.question_id ?? idx}>
                  <tr
                    onClick={() => onToggleRow(item.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onToggleRow(item.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    style={{
                      cursor: "pointer",
                      backgroundColor: isOpen ? "#f8fbff" : undefined,
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <td className="text-muted">{item.order ?? idx + 1}</td>
                    <td className="text-muted font-monospace">#{item.question_id ?? item.id}</td>
                    <td>
                      <span className="me-2 text-muted">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
                      <span className="fw-semibold">{item.question?.title ?? "-"}</span>
                    </td>
                    <td>
                      <Badge color="light" className="border">
                        {item.question_category?.title ?? "-"}
                      </Badge>
                    </td>
                    <td>{item.question ? getQuestionType(item.question) : "-"}</td>
                    <td>
                      {item.category ? (
                        aiAuditCategoryOptions[item.category] ?? item.category
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {item.severity ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          <span
                            className="rounded-circle"
                            style={{
                              width: 8,
                              height: 8,
                              display: "inline-block",
                              background: "var(--bs-" + severityColor(item.severity) + ")",
                            }}
                          />
                          <Badge color={severityColor(item.severity)} pill>
                            {capitalize(item.severity)}
                          </Badge>
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {solvingIds.has(item.id) ? (
                        <Button color="secondary" size="sm" disabled className="rounded-pill px-3">
                          <Spinner size="sm" />
                          Memproses...
                        </Button>
                      ) : (
                        <Button
                          color={!item.is_solved ? "success" : "warning"}
                          outline
                          size="sm"
                          className="rounded-pill px-3"
                          onClick={() => onSolveClick(item, !item.is_solved)}
                        >
                          {!item.is_solved ? "Selesai" : "Buka"}
                        </Button>
                      )}
                    </td>
                  </tr>
                  <TryoutAIAuditDetailRow
                    item={item}
                    isOpen={isOpen}
                    onOpenQuestionDetail={handleOpenQuestionDetail}
                  />
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>

      <TryoutAIAuditQuestionDetailModal
        isOpen={detailModal.isOpen}
        onClose={handleCloseQuestionDetail}
        question={questionDetail}
      />
    </>
  );
};

export default TryoutAIAuditTable;
export { TryoutAIAuditDetailRow };
